/**
 * Home ("desk") copy and counts — a port of mobile/src/features/home/desk-copy.ts
 * so both apps greet, count and filter the inbox the same way.
 */
import { Note } from '../types/notes';

/** A note still awaiting OCR — as opposed to finished, or failed for good. */
export function isProcessing(note: Note): boolean {
  return !note.processed && !note.failed;
}

/** Which little desk scene the Home corner doodle draws. */
export type Scene = 'morning' | 'afternoon' | 'evening' | 'night';

/** Time-of-day greeting and the corner doodle's scene. */
export function greeting(hour: number): { text: string; scene: Scene } {
  if (hour < 5) return { text: 'Burning the midnight oil', scene: 'night' };
  if (hour < 12) return { text: 'Good morning', scene: 'morning' };
  if (hour < 18) return { text: 'Good afternoon', scene: 'afternoon' };
  return { text: 'Good evening', scene: 'evening' };
}

/** First word of the user's name, for a friendlier masthead ("there" if unknown). */
export function firstName(name: string | null | undefined): string {
  return name?.trim().split(/\s+/)[0] || 'there';
}

/** What the tear-off desk calendar shows: SUN / 28 / SEP. */
export function calendarFace(date: Date): { weekday: string; day: string; month: string } {
  return {
    weekday: date.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase(),
    day: String(date.getDate()),
    month: date.toLocaleDateString(undefined, { month: 'short' }).toUpperCase(),
  };
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Local midnight at the start of `now`'s day. */
function startOfDay(now: Date): number {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

function scannedToday(note: Note, now: Date): boolean {
  return new Date(note.created_at).getTime() >= startOfDay(now);
}

/**
 * Counts for the Home stat tabs. `today` and `reading` are inbox pages — the
 * tabs you can tap to narrow the inbox, so the number always matches what the
 * tap shows. `thisWeek` is every note from the last 7 days, filed or not.
 */
export function deskTabs(
  notes: readonly Note[],
  now: Date,
): { today: number; reading: number; thisWeek: number } {
  const since = now.getTime() - WEEK_MS;
  let today = 0;
  let reading = 0;
  let thisWeek = 0;
  for (const n of notes) {
    if (n.inboxed && scannedToday(n, now)) today += 1;
    if (n.inboxed && isProcessing(n)) reading += 1;
    if (new Date(n.created_at).getTime() >= since) thisWeek += 1;
  }
  return { today, reading, thisWeek };
}

export type InboxFilter = 'all' | 'today' | 'reading';

/** The inbox narrowed by a tapped stat tab (or the calendar, for today). */
export function filterInbox(inbox: readonly Note[], filter: InboxFilter, now: Date): Note[] {
  if (filter === 'today') return inbox.filter((n) => scannedToday(n, now));
  if (filter === 'reading') return inbox.filter(isProcessing);
  return [...inbox];
}

/**
 * One friendly line under the name that says what's on the desk.
 * `waiting` = notes in the inbox; `reading` = of those, still being processed.
 */
export function deskLine(waiting: number, reading: number): string {
  if (waiting === 0) return 'Your desk is clear. Got something scribbled down?';
  if (reading > 0) return `Reading ${reading} of your pages right now — hang tight.`;
  return `${waiting} fresh ${waiting === 1 ? 'page' : 'pages'} waiting to be filed.`;
}
